import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import CheckmarkCell from '../../../../../components/tables/cellComponents/CheckmarkCell';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllPromotionalCodesQuery } from '../../../../../services/endpoints/orders/promotionalCode';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../../services/helper';
import { PromotionalCode } from '../../../../../types/orders.type';
import { formatDate } from '../../../../../utils/dateUtils';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

const defaultScope = 'active';

// RBAC-FE-ORDERS: Create/Edit/Delete promotional codes - matches backend isSuper
// Only Super and Supreme can modify
export const userCanCreateEditDelete_PromotionalCodes = (user: User | null) =>
  !!(user?.isSuper || user?.isSupremeAdmin);

// RBAC-FE-ORDERS: View promotional codes - matches backend isAdmin
// Global Admin IS included for Orders domain (unlike Finance)
export const userCanView_PromotionalCodes = (user: User | null) =>
  !!(
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin
  );

// Column definition factory to pass currentUser
const createPromoCodeColumns = (currentUser: User | null): ColumnDef<PromotionalCode>[] => [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Code',
    accessorKey: 'code'
  },
  {
    header: 'Percent Discount',
    accessorKey: 'percent_discount'
  },
  {
    header: 'Is Active',
    accessorKey: 'is_active',
    enableSorting: false,
    cell: ({ getValue }) => <CheckmarkCell value={getValue<boolean>()} />
  },
  {
    header: 'Created At',
    accessorKey: 'created_at',
    cell: ({ row }) => formatDate(new Date(row.original.created_at), 'MM/dd/yyyy h:mm zzz')
  },
  {
    header: 'Updated At',
    accessorKey: 'updated_at',
    cell: ({ row }) => formatDate(new Date(row.original.updated_at), 'MM/dd/yyyy h:mm zzz')
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        {userCanView_PromotionalCodes(currentUser) && (
          <LinkCell
            content="View"
            url={`/admin/orders/promotional-codes/${row.original.id}`}
            underline
          />
        )}
        {userCanCreateEditDelete_PromotionalCodes(currentUser) && (
          <LinkCell
            content="Edit"
            url={`/admin/orders/promotional-codes/${row.original.id}/edit`}
            underline
          />
        )}
      </Stack>
    )
  }
];

export const PromotionalCodesView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({ scope: defaultScope });
  const [scopes, setScopes] = useState<Scopes>({});
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const canAccessPromoCodes = userCanView_PromotionalCodes(currentUser);

  const { data, error, isLoading, isFetching } = useGetAllPromotionalCodesQuery(filters, {
    skip: !canAccessPromoCodes
  });
  const [promoCodes, setPromoCodes] = useState<PromotionalCode[]>([]);
  const navigate = useNavigate();

  // Create columns with currentUser context
  const promoCodeColumns = createPromoCodeColumns(currentUser);

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      type: 'number',
      label: 'ID',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'code',
      label: 'Code',
      value: getFilterValue(filters.data?.filterBy, 'code')
    },
    {
      name: 'is_active',
      type: 'number',
      element: (
        <FormSelect
          name="is_active"
          label="Is Active"
          options={[
            { value: 1, label: 'Active' },
            { value: 0, label: 'Inactive' }
          ]}
          selected_Value={[
            { value: 1, label: 'Active' },
            { value: 0, label: 'Inactive' }
          ].find(
            (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'is_active')
          )}
        />
      )
    },
    {
      name: 'created_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="created_from"
          label="Created At (From-To)"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
    },
    {
      name: 'created_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_to" />,
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
    },
    {
      name: 'updated_from',
      id: 'updated_at',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="updated_from"
          label="Updated At (From-To)"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'updated_at', 'greater_equal_than')
    },
    {
      name: 'updated_to',
      id: 'updated_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="updated_to" />,
      value: getFilterValue(filters.data?.filterBy, 'updated_at', 'lesser_equal_than')
    },
    {
      name: 'percent_discount',
      label: 'Percent discount',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'percent_discount')
    }
  ];

  useEffect(() => {
    if (!isLoading && error) {
      addToast('Error while loading promotional codes', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setPromoCodes(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  // RBAC-FE-ORDERS: Defense-in-depth guard
  if (!canAccessPromoCodes) {
    return <Forbidden />;
  }

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Promotional Codes`}
        columns={promoCodeColumns}
        data={promoCodes}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters.data?.filterBy}
        indexDownloadPath="promotional-codes"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          userCanCreateEditDelete_PromotionalCodes(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/orders/promotional-codes/new')}
            >
              New promotional code
            </SimpleButton>
          )
        }
      />
    </div>
  );
};
